import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Card, CardBody } from "../components/ui/Card";
import { Wordmark } from "../components/Brand";
import { api, ApiException } from "../lib/api";

type Step = "email" | "code";

export function SignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialError = params.get("error");

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(initialError);
  const [info, setInfo] = useState<string | null>(null);

  const codeRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (step === "code") codeRef.current?.focus();
  }, [step]);

  const [notInvited, setNotInvited] = useState(false);

  async function submitEmail(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotInvited(false);
    try {
      await api.post("/api/auth/request-link", { email });
      setStep("code");
      setInfo("We sent you a link and a 6-digit code. Either one works.");
    } catch (err) {
      if (err instanceof ApiException && err.code === "not_invited") {
        setNotInvited(true);
        setError(err.message);
      } else {
        setError(err instanceof ApiException ? err.message : "Something went wrong.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/auth/verify-code", { email, code });
      navigate("/app", { replace: true });
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "That code didn't work.");
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await api.post("/api/auth/request-link", { email });
      setInfo("New code sent. Check your inbox.");
      setCode("");
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Couldn't resend.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full flex flex-col">
      <header className="border-b border-border bg-surface">
        <div className="mx-auto max-w-5xl px-5 h-14 flex items-center">
          <Link to="/" className="block"><Wordmark size="sm" /></Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-10">
        <Card className="w-full max-w-md">
          <CardBody>
            {step === "email" ? (
              <>
                <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
                <p className="mt-1 text-sm text-text-muted">
                  We'll email you a one-time link and a 6-digit code. No password.
                </p>

                <form onSubmit={submitEmail} className="mt-6 space-y-4" noValidate>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      required
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      aria-invalid={!!error}
                    />
                  </div>

                  {error && (
                    <div role="alert" className="rounded-md border border-danger/30 bg-red-50 text-danger text-sm px-3 py-2">
                      {error}
                      {notInvited && (
                        <div className="mt-2">
                          <Link to={`/request-access?email=${encodeURIComponent(email)}`} className="text-primary font-medium hover:underline">
                            Get started →
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  <Button type="submit" size="lg" loading={busy} className="w-full">
                    {busy ? "Sending…" : "Send sign-in link"}
                  </Button>

                  <p className="text-center text-xs text-text-faint pt-1">
                    New here?{" "}
                    <Link to="/request-access" className="text-primary hover:underline">Get started</Link>.
                    {" "}By signing in you agree to our{" "}
                    <Link to="/terms" className="text-primary hover:underline">Terms</Link>{" "}and{" "}
                    <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
                  </p>
                </form>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-semibold tracking-tight">Check your email</h1>
                <p className="mt-1 text-sm text-text-muted">
                  Sent to <span className="font-medium text-text">{email}</span>.
                </p>
                {info && (
                  <div className="mt-4 rounded-md bg-success-soft border border-green-200 text-success text-sm px-3 py-2">{info}</div>
                )}
                <form onSubmit={submitCode} className="mt-5 space-y-4" noValidate>
                  <div>
                    <Label htmlFor="code">Enter the 6-digit code</Label>
                    <Input
                      ref={codeRef}
                      id="code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="\d{6}"
                      maxLength={7}
                      placeholder="123 456"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D+/g, "").slice(0, 6))}
                      className="text-center text-2xl tracking-[0.4em] font-semibold tabular-nums"
                      aria-invalid={!!error}
                    />
                  </div>

                  {error && (
                    <div role="alert" className="rounded-md border border-danger/30 bg-red-50 text-danger text-sm px-3 py-2">
                      {error}
                    </div>
                  )}

                  <Button type="submit" size="lg" loading={busy} disabled={code.length !== 6} className="w-full">
                    Sign in
                  </Button>
                </form>

                <div className="mt-4 text-center text-sm text-text-muted">
                  Or just click the link in the email.
                </div>

                <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-sm">
                  <button type="button" onClick={() => { setStep("email"); setCode(""); setError(null); setInfo(null); }} className="text-text-muted hover:text-text">
                    ← Different email
                  </button>
                  <button type="button" onClick={resend} disabled={busy} className="text-primary hover:underline disabled:opacity-50">
                    Resend code
                  </button>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </main>
    </div>
  );
}
